import { createRouter, createWebHistory } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { useCustomerStore } from '@/stores/customer'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      alias: '/login',
      name: 'login',
      component: () => import('@/views/LoginView.vue')
    },
    {
      path: '/search',
      name: 'search',
      component: () => import('@/views/SearchView.vue')
    },
    {
      path: '/support',
      name: 'support',
      component: () => import('@/views/SupportView.vue'),
      beforeEnter: () => {
        if (!useCustomerStore().getCustomer) return {name: 'search'}
      }
    },
    {
      path: '/sales',
      name: 'sales',
      component: () => import('@/views/SalesView.vue'),
      beforeEnter: () => {
        if (!useCustomerStore().getCustomer) return {name: 'search'}
      }
    },
    {
      path: '/ticket',
      name: 'ticket',
      component: () => import('@/views/TicketView.vue'),
      beforeEnter: () => {
        if (!useCustomerStore().getCustomer) return {name: 'search'}
      }
    },
    {
      path: '/admin',
      name: 'admin',
      component: () => import('@/views/AdminView/index.vue'),
      // Static redirect would send every /admin visit to admin-users, which is
      // isAdmin-gated -- a product_admin/qa_tools-only user (no isAdmin) would be
      // admitted past canAccessAdminGroup only to get bounced by the child guard.
      // Mirrors landingRoute's admin-section priority instead.
      redirect: () => {
        const userStore = useUserStore()
        if (userStore.isAdmin) return { name: 'admin-users' }
        if (userStore.canAccessProductAdmin) return { name: 'admin-tfns' }
        if (userStore.canViewProductAdmin) return { name: 'admin-products' }
        if (userStore.canAccessQaTools) return { name: 'admin-qa' }
        if (userStore.canAccessMarketingAdmin) return { name: 'admin-source-codes' }
        return { name: 'admin-users' }
      },
      children: [
        {
          path: 'users',
          name: 'admin-users',
          component: () => import('@/views/AdminView/AdminUsersView.vue')
        },
        {
          path: 'teams',
          name: 'admin-teams',
          component: () => import('@/views/AdminView/AdminTeamsView.vue')
        },
        {
          path: 'roles',
          name: 'admin-roles',
          component: () => import('@/views/AdminView/AdminRolesView.vue')
        },
        {
          path: 'qa',
          name: 'admin-qa',
          component: () => import('@/views/AdminView/AdminQAView.vue')
        },
        {
          path: 'callouts',
          name: 'admin-callouts',
          component: () => import('@/views/AdminView/AdminCalloutsView.vue')
        },
        {
          path: 'tfns',
          name: 'admin-tfns',
          component: () => import('@/views/AdminView/AdminTfnsView.vue')
        },
        {
          path: 'contact-reasons',
          name: 'admin-contact-reasons',
          component: () => import('@/views/AdminView/AdminContactReasonsView.vue')
        },
        {
          path: 'offers',
          name: 'admin-offers',
          component: () => import('@/views/AdminView/AdminOffersView.vue')
        },
        {
          path: 'retention-offers',
          name: 'admin-retention-offers',
          component: () => import('@/views/AdminView/AdminRetentionOffersView.vue')
        },
        {
          path: 'products',
          name: 'admin-products',
          component: () => import('@/views/AdminView/AdminProductsView.vue')
        },
        {
          path: 'chat-domains',
          name: 'admin-chat-domains',
          component: () => import('@/views/AdminView/AdminChatDomainsView.vue')
        },
        {
          path: 'source-codes',
          name: 'admin-source-codes',
          component: () => import('@/views/AdminView/AdminSourceCodesView.vue')
        },
        {
          path: 'marketing-imports',
          name: 'admin-marketing-imports',
          component: () => import('@/views/AdminView/AdminMarketingImportsView.vue')
        }
      ]
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      redirect: () => ({ name: 'search' })
    }
  ]
})

// The "Product" admin group — TFNs/Callouts/Offers share one gate (canAccessProductAdmin).
// admin-products is the odd one out: it also admits qa_tools (view-only), matching
// cash-server's admin/products route group, so it's checked separately against
// canViewProductAdmin rather than folded into this list.
const PRODUCT_ADMIN_ROUTES = ['admin-tfns', 'admin-callouts', 'admin-offers', 'admin-retention-offers', 'admin-contact-reasons', 'admin-chat-domains']

// The "Marketing" admin group -- Source Codes and CSV Import (CASH-791) today.
const MARKETING_ADMIN_ROUTES = ['admin-source-codes', 'admin-marketing-imports']

// Mirrors the gates below, so a `landing_page` permission that points at a route the
// role can't actually reach is caught instead of redirecting to itself forever.
const canAccessRoute = (name, userStore) => {
  if (['search', 'support', 'sales', 'ticket'].includes(name)) return userStore.canAccessSearch
  if (PRODUCT_ADMIN_ROUTES.includes(name)) return userStore.canAccessProductAdmin
  if (name === 'admin-products') return userStore.canViewProductAdmin
  if (name === 'admin-qa') return userStore.canAccessQaTools
  if (MARKETING_ADMIN_ROUTES.includes(name)) return userStore.canAccessMarketingAdmin
  if (name.includes('admin')) return userStore.isAdmin
  return true
}

router.beforeEach((to, from, next) => {
  const userStore = useUserStore()
  if (to.name !== 'login' && !userStore.loggedIn)
    return next({ name: 'login', query: { error: 'notLoggedIn', redirect: to.fullPath } })

  if (userStore.loggedIn) {
    // Role permissions are the source of truth for these gates: `customer_support`
    // controls the Search/Support/Sales/Ticket flow, `product_admin` (or isAdmin)
    // controls the Product admin group (TFNs/Callouts/Offers), `qa_tools` controls
    // the QA Tools admin page and additionally grants view-only access to admin-products
    // (Admin/product_admin/qa_tools -- no team_name fallback anywhere in this guard).
    // landingName is where a user without access to the page they're headed to gets
    // bounced instead - falling back to 'login' when a role has no accessible page at all.
    const landingName = userStore.landingRoute || 'login'
    const toLanding = (query) => {
      // Fail closed: a misconfigured landing_page must not throw inside the guard,
      // since an uncaught exception here breaks routing entirely. landing_page comes
      // from the permissions JSONB, so it isn't guaranteed to be a string — guard
      // before canAccessRoute() calls .includes() on it.
      if (landingName !== 'login' && (typeof landingName !== 'string' || !canAccessRoute(landingName, userStore)))
        return next({ name: 'login', query: { error: 'noAccess' } })
      return next({ name: landingName, query })
    }

    // Single source of truth for the gate itself is canAccessRoute (defined above) --
    // this only decides which error query the two route groups show on the bounce.
    if (!canAccessRoute(to.name, userStore)) {
      const isSearchGroup = ['search', 'support', 'sales', 'ticket'].includes(to.name)
      return toLanding(isSearchGroup ? (landingName === 'login' ? { error: 'noAccess' } : undefined) : { error: 'notAdmin' })
    }
  }

  next()
})

export default router
